import { createContext, useContext, useState } from 'react';
import { createTheme } from '@mui/material/styles';

const ThemeContext = createContext();

export const useThemeContext = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useThemeContext must be used within a ThemeProvider');
  }
  return context;
};

// Theme definitions
export const themes = {
  retro: {
    name: 'Retro',
    muiTheme: createTheme({
      palette: {
        mode: 'light',
        primary: {
          main: '#8B4513', // Saddle brown
          light: '#A0522D',
          dark: '#654321',
        },
        secondary: {
          main: '#4682B4', // Steel blue
          light: '#87CEEB',
          dark: '#2F4F4F',
        },
        background: {
          default: '#F5E6D3',
          paper: '#FAEBD7',
        },
        text: {
          primary: '#3E2723',
          secondary: '#5D4037',
        },
        divider: '#D7CCC8',
      },
      typography: {
        fontFamily: '"Georgia", "Times New Roman", serif',
        h1: { color: '#8B4513' },
        h2: { color: '#8B4513' },
        h3: { color: '#8B4513' },
        h4: { color: '#8B4513' },
        h5: { color: '#8B4513' },
      },
      components: {
        MuiCard: {
          styleOverrides: {
            root: {
              backgroundColor: '#FAEBD7',
              borderRadius: '12px',
              border: '2px solid #D7CCC8',
            },
          },
        },
        MuiButton: {
          styleOverrides: {
            contained: {
              backgroundColor: '#4682B4',
              color: '#FFFFFF',
              '&:hover': { backgroundColor: '#2F4F4F' },
              borderRadius: '8px',
              fontWeight: 'bold',
            },
          },
        },
        MuiSelect: {
          styleOverrides: {
            root: {
              backgroundColor: '#FAEBD7',
              borderRadius: '8px',
            },
          },
        },
      },
    }),
    heatmapColors: {
      background: '#FAEBD7',
      border: '#D7CCC8',
      textPrimary: '#3E2723',
      textSecondary: '#5D4037',
      heatmapGradient: 'rgba(70, 130, 180, {intensity})',
      legendColors: {
        none: '#FAEBD7',
        low: 'rgba(70, 130, 180, 0.3)',
        medium: 'rgba(70, 130, 180, 0.7)',
        high: 'rgba(70, 130, 180, 1)',
      }
    }
  },
  
  light: {
    name: 'Light',
    muiTheme: createTheme({
      palette: {
        mode: 'light',
        primary: { main: '#1976d2' },
        secondary: { main: '#dc004e' },
        background: {
          default: '#ffffff',
          paper: '#f5f5f5',
        },
      },
      typography: {
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
      },
      components: {
        MuiCard: {
          styleOverrides: {
            root: {
              borderRadius: '8px',
              border: '1px solid #e0e0e0',
            },
          },
        },
      },
    }),
    heatmapColors: {
      background: '#ffffff',
      border: '#e0e0e0',
      textPrimary: '#333333',
      textSecondary: '#666666',
      heatmapGradient: 'rgba(25, 118, 210, {intensity})',
      legendColors: {
        none: '#f5f5f5',
        low: 'rgba(25, 118, 210, 0.3)',
        medium: 'rgba(25, 118, 210, 0.7)',
        high: 'rgba(25, 118, 210, 1)',
      }
    }
  },
  
  dark: {
    name: 'Dark',
    muiTheme: createTheme({
      palette: {
        mode: 'dark',
        primary: { main: '#90caf9' },
        secondary: { main: '#f48fb1' },
        background: {
          default: '#121212',
          paper: '#1e1e1e',
        },
        text: {
          primary: '#ffffff',
          secondary: '#aaaaaa',
        },
      },
      typography: {
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
      },
      components: {
        MuiCard: {
          styleOverrides: {
            root: {
              backgroundColor: '#1e1e1e',
              borderRadius: '8px',
              border: '1px solid #333333',
            },
          },
        },
      },
    }),
    heatmapColors: {
      background: '#1e1e1e',
      border: '#333333',
      textPrimary: '#ffffff',
      textSecondary: '#aaaaaa',
      heatmapGradient: 'rgba(144, 202, 249, {intensity})',
      legendColors: {
        none: '#2a2a2a',
        low: 'rgba(144, 202, 249, 0.3)',
        medium: 'rgba(144, 202, 249, 0.7)',
        high: 'rgba(144, 202, 249, 1)',
      }
    }
  },
  
  xbox: {
    name: 'Xbox',
    muiTheme: createTheme({
      palette: {
        mode: 'dark',
        primary: { main: '#107C10' }, // Xbox green
        secondary: { main: '#FFFFFF' },
        background: {
          default: '#0E1E0E',
          paper: '#1A2F1A',
        },
        text: {
          primary: '#FFFFFF',
          secondary: '#B0B0B0',
        },
      },
      typography: {
        fontFamily: '"Segoe UI", Tahoma, Geneva, Verdana, sans-serif',
        h1: { color: '#107C10' },
        h2: { color: '#107C10' },
        h3: { color: '#107C10' },
        h4: { color: '#107C10' },
        h5: { color: '#107C10' },
      },
      components: {
        MuiCard: {
          styleOverrides: {
            root: {
              backgroundColor: '#1A2F1A',
              borderRadius: '4px',
              border: '2px solid #107C10',
            },
          },
        },
        MuiButton: {
          styleOverrides: {
            contained: {
              backgroundColor: '#107C10',
              color: '#FFFFFF',
              '&:hover': { backgroundColor: '#0F5B0F' },
              borderRadius: '4px',
              fontWeight: 'bold',
              textTransform: 'uppercase',
            },
          },
        },
      },
    }),
    heatmapColors: {
      background: '#1A2F1A',
      border: '#107C10',
      textPrimary: '#FFFFFF',
      textSecondary: '#B0B0B0',
      heatmapGradient: 'rgba(16, 124, 16, {intensity})',
      legendColors: {
        none: '#2A2A2A',
        low: 'rgba(16, 124, 16, 0.3)',
        medium: 'rgba(16, 124, 16, 0.7)',
        high: 'rgba(16, 124, 16, 1)',
      }
    }
  },
  
  ps5: {
    name: 'PS5',
    muiTheme: createTheme({
      palette: {
        mode: 'dark',
        primary: { main: '#0070D1' }, // PlayStation blue
        secondary: { main: '#FFFFFF' },
        background: {
          default: '#000000',
          paper: '#1A1A1A',
        },
        text: {
          primary: '#FFFFFF',
          secondary: '#CCCCCC',
        },
      },
      typography: {
        fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
        h1: { color: '#0070D1', fontWeight: 'bold' },
        h2: { color: '#0070D1', fontWeight: 'bold' },
        h3: { color: '#0070D1', fontWeight: 'bold' },
        h4: { color: '#0070D1', fontWeight: 'bold' },
        h5: { color: '#0070D1', fontWeight: 'bold' },
      },
      components: {
        MuiCard: {
          styleOverrides: {
            root: {
              backgroundColor: '#1A1A1A',
              borderRadius: '16px',
              border: '1px solid #333333',
              boxShadow: '0 4px 20px rgba(0, 112, 209, 0.3)',
            },
          },
        },
        MuiButton: {
          styleOverrides: {
            contained: {
              backgroundColor: '#0070D1',
              color: '#FFFFFF',
              '&:hover': { backgroundColor: '#005BB5' },
              borderRadius: '20px',
              fontWeight: 'bold',
              padding: '10px 20px',
            },
          },
        },
      },
    }),
    heatmapColors: {
      background: '#1A1A1A',
      border: '#0070D1',
      textPrimary: '#FFFFFF',
      textSecondary: '#CCCCCC',
      heatmapGradient: 'rgba(0, 112, 209, {intensity})',
      legendColors: {
        none: '#2A2A2A',
        low: 'rgba(0, 112, 209, 0.3)',
        medium: 'rgba(0, 112, 209, 0.7)',
        high: 'rgba(0, 112, 209, 1)',
      }
    }
  }
};

export const ThemeContextProvider = ({ children }) => {
  const [currentTheme, setCurrentTheme] = useState('retro');
  
  const changeTheme = (themeName) => {
    setCurrentTheme(themeName);
  };
  
  const value = {
    currentTheme,
    changeTheme,
    theme: themes[currentTheme],
    availableThemes: Object.keys(themes),
  };
  
  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};